import { ILeagueConfig } from 'fitbeat.models';
import { generateUniqueId } from 'fitbeat.gym-core';
import React, { useEffect, useState } from 'react';

interface ILeagueListEditorProps {
  title: string;
  leagues: ILeagueConfig[];
  selectedLeagueIndex?: number | null;
  onAddLeague: (league: ILeagueConfig) => void;
  onUpdateLeague: (index: number, league: ILeagueConfig) => void;
  onDeleteLeague: (index: number) => void;
  onClearSelection?: () => void;
}

const LeagueListEditor: React.FC<ILeagueListEditorProps> = ({
  title,
  leagues,
  selectedLeagueIndex = null,
  onAddLeague,
  onUpdateLeague,
  onDeleteLeague,
  onClearSelection,
}) => {
  const isEditMode = selectedLeagueIndex !== null && selectedLeagueIndex >= 0 && selectedLeagueIndex < leagues.length;

  const [newLeague, setNewLeague] = useState<ILeagueConfig>({
    id: '',
    displayName: '',
  });

  useEffect(() => {
    if (isEditMode) {
      setNewLeague(leagues[selectedLeagueIndex]);
    } else {
      setNewLeague({ id: '', displayName: '' });
    }
  }, [selectedLeagueIndex]);

  const handleSubmit = () => {
    if (isEditMode) {
      onUpdateLeague(selectedLeagueIndex, newLeague);
      onClearSelection?.();
    } else {
      if (newLeague.displayName.trim()) {
        const league: ILeagueConfig = {
          id: newLeague.id,
          displayName: newLeague.displayName.trim(),
          primaryColour: newLeague.primaryColour,
        };
        onAddLeague(league);
        setNewLeague({ id: '', displayName: '' });
      }
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isEditMode && onClearSelection) {
      onClearSelection();
    } else {
      setNewLeague({ id: '', displayName: '' });
    }
    e.currentTarget.blur();
  };

  return (
    <div style={styles.formColumn}>
      <div style={styles.scrollableContent}>
        <h3 style={styles.columnHeader}>
          {title}
        </h3>
        <div style={styles.inputGroup}>
          <label style={styles.label}>League id</label>
          <input
            type='text'
            value={newLeague.id}
            placeholder='Set League Name'
            style={styles.inputField}
            disabled
          />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>League display name</label>
          <input
            type='text'
            value={newLeague.displayName}
            onChange={(e) =>
              setNewLeague({
                ...newLeague,
                displayName: e.target.value,
                id: isEditMode ? newLeague.id : generateUniqueId(e.target.value, leagues, selectedLeagueIndex),
              })}
            placeholder='Set League Name'
            style={styles.inputField}
          />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Primary Colour</label>
          <input
            type='text'
            value={newLeague.primaryColour || ''}
            onChange={(e) =>
              setNewLeague({
                ...newLeague,
                primaryColour: e.target.value === '' ? undefined : e.target.value,
              })}
            placeholder='Primary Colour (optional)'
            style={styles.inputField}
          />
        </div>
      </div>
      <div style={styles.buttonRow}>
        <button type='button' onClick={handleSubmit} className='jr-btn-default'>
          {isEditMode ? 'Update' : 'Create'}
        </button>
        <button type='button' onClick={handleCancel} className='jr-btn-default-outlined'>
          Cancel
        </button>
        {isEditMode && (
          <button
            type='button'
            onClick={() => {
              if (selectedLeagueIndex !== null && selectedLeagueIndex !== undefined) {
                onDeleteLeague(selectedLeagueIndex);
                onClearSelection?.();
              }
            }}
            className='jr-btn-default-outlined'
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    marginRight: '16px',
    padding: '16px',
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  columnHeader: {
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '12px',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: '4px',
    color: '#333',
  },
  inputField: {
    width: '100%',
    padding: '8px',
    marginBottom: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '16px',
  },
};

export default LeagueListEditor;
